import { Product } from '@danone-global/ct/interfaces'
import { AddToCartIcon } from '@danone-global/internal/react/components/icons'
import { useStyles as useLineItemStyles } from '@danone-global/internal/react/components/line-item'
import Price from '@danone-global/internal/react/components/price'
import {
  Container,
  CtAsset,
  CtImage,
  Grid,
  IconButton,
  Typography,
} from '@danone-global/internal/react/components/ui'
import { Theme, useCore } from '@danone-global/internal/react/core'
import { localisedProductName } from '@danone-global/internal/react/core'
import { useAddProductToCart } from '@danone-global/internal/react/modules/product'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'

export interface Props {
  product: Product

  withAnalytics: boolean

  variant: 'default' | 'quick-view'
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(2),

      [theme.breakpoints.mobile()]: {
        border: 'none',
      },
    },

    'quick-view': {},

    default: {
      [theme.breakpoints.mobile()]: {
        padding: 0,

        '& p': {
          margin: theme.spacing(0, 0, 1),
        },
      },

      [theme.breakpoints.up('sm')]: {
        '& $container': {
          flexDirection: 'column',
        },

        '& p': {
          margin: theme.spacing(2, 0),
        },

        '& $subContainer': {
          alignItems: 'flex-end',
        },
      },
    },

    container: {},

    imageContainer: {
      [theme.breakpoints.up('sm')]: {
        margin: 0,

        '& img': {
          height: theme.utils.createStyle(
            theme.utils.getSize(100, { forceUnit: true }),
            '!important',
          ),
          maxHeight: theme.utils.createStyle(
            theme.utils.getSize(100, { forceUnit: true }),
            '!important',
          ),
          width: theme.utils.createStyle(
            theme.utils.getSize(100, { forceUnit: true }),
            '!important',
          ),
        },
      },
    },

    subContainer: {},
  }),
  { name: 'RelatedProduct' },
)

const RelatedProduct: React.FC<Props> = ({
  variant,
  product,
  withAnalytics,
}) => {
  const {
    masterData: {
      current: {
        masterVariant: { sku, images, price },
      },
    },
  } = product
  const { localeConfig } = useCore()

  const addProductToCart = useAddProductToCart({
    isUpSell: true,
    withAnalytics,
    cart: undefined,
  })
  const classes = useStyles()
  const lineItemClasses = useLineItemStyles()

  const productName = localisedProductName(
    product.masterData.current,
    localeConfig.locale,
  )

  /** DTCPB-4318 - Image localization from PIM to CT */
  let assetImages = []

  const asset = product?.masterData?.current?.masterVariant?.assets?.find((i) =>
    i?.tags?.includes(localeConfig.locale),
  )

  if (
    asset &&
    asset?.custom?.customFieldsRaw?.find(
      (f) => f.name === 'imagePosition' && f.value === 0,
    )
  ) {
    assetImages = asset.sources
  }

  const handleAddProduct = React.useCallback(
    () =>
      addProductToCart(
        product.masterData.current.masterVariant,
        null,
        null,
        1,
        productName,
        product.masterData.current.categories,
      ),
    [addProductToCart, product, productName],
  )

  const Wrapper = variant === 'default' ? Container : Grid

  const contentQuantity =
    product.masterData.current.masterVariant?.attributesRaw
      ?.filter((item) => item.name === 'contentquantity')
      .map((quantity) => quantity.value)
      .join('')
  const measurementUnit =
    product.masterData.current.masterVariant?.attributesRaw
      ?.filter((item) => item.name === 'measurementUnit')
      .map((unit) => unit.value.label)
      .join('')

  return (
    <Grid
      data-testid={`related-product-${sku}`}
      md={variant === 'default' ? 4 : 12}
      sm={variant === 'default' ? 6 : 12}
      xs={12}
      flex
      item
    >
      <Wrapper
        className={clsx(lineItemClasses.root, classes.root, classes[variant])}
        justify="space-between"
        flex
      >
        <Grid
          alignItems="center"
          className={classes.container}
          direction="row"
          flex
          item
        >
          <Grid
            alignContent="center"
            className={clsx(
              lineItemClasses.imageContainer,
              classes.imageContainer,
            )}
            item
          >
            {assetImages.length > 0 ? (
              <CtAsset
                height="auto"
                maxHeight="fit-content"
                width={70}
                {...assetImages[0]}
              />
            ) : images.length > 0 ? (
              <CtImage
                height="auto"
                maxHeight="fit-content"
                width={70}
                {...images[0]}
              />
            ) : (
              <div
                style={{ width: '70px', minHeight: '100px', height: 'auto' }}
              />
            )}
          </Grid>

          <Grid
            className={lineItemClasses.nameContainer}
            direction="column"
            container
            flex
          >
            <Grid>
              <Typography
                color="primary-dark"
                fontWeight="bold"
                variant="body"
                noGutters
              >
                {productName}
              </Typography>
            </Grid>

            <Grid
              alignItems="center"
              className={clsx(
                lineItemClasses.subContainer,
                classes.subContainer,
              )}
              direction="row"
              flex
              item
            >
              <Grid
                className={lineItemClasses.priceContainer}
                justify="flex-start"
                container
              >
                <Price
                  testId={`line-item-${sku}-price`}
                  small
                  {...(price.discounted ? price.discounted.value : price.value)}
                  additionalPrice={price.discounted ? price.value : undefined}
                  contentQuantity={contentQuantity && Number(contentQuantity)}
                  measurementUnit={measurementUnit}
                />
              </Grid>

              <Grid flex>
                <IconButton
                  color="white"
                  data-testid={`btn.related-product-${sku}.add`}
                  onClick={handleAddProduct}
                  variant="brand"
                >
                  <AddToCartIcon width={24} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Wrapper>
    </Grid>
  )
}

export default React.memo(RelatedProduct)
